<script lang="ts">
const MIN_SEGMENT_PIXELS = 2
</script>
<script setup lang="ts">
const props = defineProps({
  file: null,
  matchedChunks: null,
  tooltipWidth: null,
  selected: { type: Boolean }
})


/**
 * Creates segments that are tuples of [%start, %end], combining adjacent segments.
 * NOTE: We need a minimum percentage for segments to be visible, perhaps 2%
 */
const segments = computed(() => {
  const segments: [number, number][] = []

  const numPages = props.file.mediaPages()
  const durationMs = props.file.mediaDurationMs()
  const lineCount = props.file.textLineCount()

  if (isDefined(numPages)) {
    const chunks = props
      .matchedChunks
      .filter(chunk => chunk.chunkType === "polyEmbedV1Page")
      .filter(chunk => isDefined(chunk.pageNumber))
      .sort((a, b) => a.pageNumber! - b.pageNumber!)

    for (const chunk of chunks) {
      if (chunk.chunkType !== "polyEmbedV1Page")
        continue
      if (!isDefined(chunk.pageNumber))
        continue
      const start = ((chunk.pageNumber - 1) / numPages) * 100
      const actualEnd = (chunk.pageNumber / numPages) * 100
      const minEnd = start + (MIN_SEGMENT_PIXELS / props.tooltipWidth) * 100
      const end = Math.max(actualEnd, minEnd)

      // check if we need to merge with the previous segment
      const prior = segments.pop()
      if (prior && prior[1] >= start)
        segments.push([prior[0], end])
      else if (prior)
        segments.push(prior, [start, end])
      else
        segments.push([start, end])
    }
  }
  else if (isDefined(durationMs)) {
    const chunks = props
      .matchedChunks
      .filter(chunk => chunk.chunkType === "polyEmbedV1Page")
      .filter(chunk => isDefined(chunk.startOffsetSec) && isDefined(chunk.endOffsetSec))
      .sort((a, b) => a.startOffsetSec! - b.startOffsetSec!)
    const durationSec = durationMs / 1000

    for (const chunk of chunks) {
      if (chunk.chunkType !== "polyEmbedV1Page")
        continue
      if (!isDefined(chunk.startOffsetSec) || !isDefined(chunk.endOffsetSec))
        continue
      const start = ((chunk.startOffsetSec / durationSec) * 100)
      const actualEnd = ((chunk.endOffsetSec / durationSec) * 100)
      const minEnd = start + (MIN_SEGMENT_PIXELS / props.tooltipWidth) * 100
      const end = Math.max(actualEnd, minEnd)

      // check if we need to merge with the previous segment
      const prior = segments.pop()
      if (prior && prior[1] >= start)
        segments.push([prior[0], end])
      else if (prior)
        segments.push(prior, [start, end])
      else
        segments.push([start, end])
    }
  }

  else if (isDefined(lineCount)) {
    const chunks = props
      .matchedChunks
      .filter(chunk => chunk.chunkType === "polyEmbedV1Page")
      .filter(chunk => isDefined(chunk.startLine) && isDefined(chunk.endLine))
      .sort((a, b) => a.startLine! - b.startLine!)

    for (const chunk of chunks) {
      if (chunk.chunkType !== "polyEmbedV1Page")
        continue
      if (!isDefined(chunk.startLine) || !isDefined(chunk.endLine))
        continue
      const start = ((chunk.startLine / lineCount) * 100)
      const actualEnd = ((chunk.endLine / lineCount) * 100)
      const minEnd = start + (MIN_SEGMENT_PIXELS / props.tooltipWidth) * 100
      const end = Math.max(actualEnd, minEnd)

      // check if we need to merge with the previous segment
      const prior = segments.pop()
      if (prior && prior[1] >= start)
        segments.push([prior[0], end])
      else if (prior)
        segments.push(prior, [start, end])
      else
        segments.push([start, end])
    }
  }

  return segments
})
</script>

<template>
  <div class="container" :style="{ width: px(props.tooltipWidth + 2) }">
    <tooltip-below
      :tooltip-width
      :tooltip-height="24"
      selected
    >
      <div class="content">
        <div class="segment-container">
          <div
            v-for="[start, end], key in segments"
            :key
            class="segment"
            :style="{
              left: `${start}%`,
              width: `${end - start}%`,
            }"
          />
        </div>
      </div>
    </tooltip-below>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.content
  @apply w-full h-full p-0.5
.segment-container
  @apply relative w-full h-full

.segment
  @apply h-full border border-1 border-$global-blue-primary bg-$global-blue-secondary absolute rounded-[3px]
</style>
